import React from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import CrossIcon from '@atlaskit/icon/utility/cross';
import { Flex, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from '../../common/messages';

import type { PreviewPanelHeaderProps } from './types';

const styles = cssMap({
	header: {
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		borderBlockEndStyle: 'solid',
		borderBlockEndWidth: token('border.width'),
		borderColor: token('color.border'),
		marginBottom: token('space.200'),
	},
	childWrapper: {
		flexGrow: 1,
	},
});

export const PreviewPanelHeader = ({ children, onClose, testId, url, analyticsClient, parentProduct, panelType }: PreviewPanelHeaderProps) => {
	const { formatMessage } = useIntl();

	const onPanelClose = () => {
		analyticsClient?.sendTrackEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'preview-panel-close',
			source: 'preview-panel',
			attributes: {
				parentProduct,
				panelType
			},
		})
		onClose();
	};

	const onOpenInNewWindow = () => {
		analyticsClient?.sendTrackEvent({
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'preview-panel-open-in-new-window',
			source: 'preview-panel',
			attributes: {
				parentProduct,
				panelType
			},
		});
		window.open(url);
	}

	return (
		<Inline xcss={styles.header} testId={testId} alignBlock="center" space="space.100">
			<Inline
				xcss={styles.childWrapper}
				spread="space-between"
				alignBlock="center"
				space="space.100"
			>
				{children}
			</Inline>
			<Flex gap="space.050">
				<IconButton
					icon={LinkExternalIcon}
					appearance="subtle"
					label={formatMessage(messages.previewPanelOpenInNewWindowLabel)}
					onClick={onOpenInNewWindow}
					testId={testId && `${testId}.open-in-new-window-button`}
				/>
				<IconButton
					icon={CrossIcon}
					label={formatMessage(messages.previewPanelCloseButtonLabel)}
					onClick={onPanelClose}
					testId={testId && `${testId}.close-button`}
				/>
			</Flex>
		</Inline>
	);
};
